import { React, useContext, useEffect, useState, useId } from "react";
import { AppContext } from "../App";
import { callApi, getInlineLoader } from "../Helpers";
import { FiUser, FiMail, FiPhone, FiMapPin, FiCalendar, FiSave, FiArrowLeft, FiLock } from "react-icons/fi";

export default function EditApplicantProfile() {
  const appContext = useContext(AppContext);
  const scopeId = useId().replace(/:/g, "");

  /* Place states here */
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  /* Working Controlled Form Input States */
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [physicalAddress, setPhysicalAddress] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("Male");

  async function init() {
    setReady(false);
    await Promise.all([
      getData(),
    ]);
    setReady(true);
  }

  async function getData() {
    await callApi('jobapplicant/get-current-applicant-data', {}).then((response) => {
      if (response && response.status === 1) {
        setData(response.data);
      } else {
        appContext?.tellError?.('Could not fetch profile records');
      }
    }).catch(() => {
      appContext?.tellError?.('Network error connecting to registry server');
    });
  }

  async function handleUpdate() {
    if (!firstName.trim()) return appContext?.tellError?.("First name field is required.");
    if (!middleName.trim()) return appContext?.tellError?.("Middle name is required.");
    if (!lastName.trim()) return appContext?.tellError?.("Last name field is required.");
    if (!phone.trim()) return appContext?.tellError?.("Mobile phone number is required.");
    if (!physicalAddress.trim()) return appContext?.tellError?.("Physical Address is required.");
    if (!dob.trim()) return appContext?.tellError?.("Date of Birth is required.");
    if (!gender.trim()) return appContext?.tellError?.("Select Gender.");

    setLoading(true);
    const payload = {
      firstName,
      middleName,
      lastName,
      phone,
      physicalAddress,
      dob,
      gender
    };

    await callApi('jobapplicant/update-account-details', payload).then((response) => {
      if (response && response.status === 1) {
        appContext?.tellMessage?.('Profile records updated successfully.');
        appContext?.navBack?.();
      } else {
        appContext?.tellError?.(response.msg || "Failed to update profile data.");
      }
    }).catch(() => {
      appContext?.tellError?.("Network serialization interface timeout error.");
    });
    setLoading(false);
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (data) {
      setFirstName(data.firstName || "");
      setMiddleName(data.middleName || "");
      setLastName(data.lastName || "");
      setEmail(data.email || "");
      setPhone(data.phone || "");
      setPhysicalAddress(data.physicalAddress || "");
      setGender(data.gender || "Male");
      
      if (data.dob) {
        setDob(data.dob.split("T")[0]);
      }
    }
  }, [data]);

  useEffect(() => {
    appContext?.setShowOverlayLoader?.(loading);
    return () => {
      appContext?.setShowOverlayLoader?.(false);
    };
  }, [loading]);

  if (ready) {
    return (
      <>
        <style>{`
          .${scopeId}-wrapper {
            background-color: #091118;
            color: #f3f4f6;
            min-height: 100%;
            font-family: system-ui, -apple-system, sans-serif;
            padding: 24px;
          }

          /* --- Header Panel --- */
          .${scopeId}-header-block {
            display: flex;
            align-items: center;
            gap: 16px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.08);
            padding-bottom: 20px;
            margin-bottom: 28px;
          }

          .${scopeId}-back-btn {
            background: transparent;
            border: 1px solid rgba(255, 255, 255, 0.15);
            color: #ffffff;
            width: 38px;
            height: 38px;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .${scopeId}-back-btn:hover {
            background: rgba(255, 255, 255, 0.05);
            border-color: #ffffff;
          }

          .${scopeId}-panel-title {
            font-size: 1.35rem;
            font-weight: 800;
            color: #ffffff;
            margin: 0 0 4px 0;
            letter-spacing: -0.02em;
          }

          .${scopeId}-panel-subtitle {
            color: rgba(255, 255, 255, 0.5);
            font-size: 0.8rem;
            margin: 0;
          }

          /* --- Dynamic Grid Form --- */
          .${scopeId}-form-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }

          .${scopeId}-col-full {
            grid-column: span 2;
          }

          .${scopeId}-field-group {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .${scopeId}-label {
            font-size: 0.75rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: rgba(255, 255, 255, 0.6);
          }

          .${scopeId}-input-container {
            position: relative;
            width: 100%;
          }

          .${scopeId}-icon {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            color: rgba(255, 255, 255, 0.35);
            display: flex;
            align-items: center;
          }

          .${scopeId}-input-field {
            width: 100%;
            background: #050b11;
            border: 1px solid rgba(255, 255, 255, 0.12);
            border-radius: 4px;
            padding: 12px 12px 12px 38px;
            color: #ffffff;
            font-size: 0.9rem;
            outline: none;
            transition: all 0.2s ease;
          }

          .${scopeId}-input-field:focus {
            border-color: var(--secondaryColor, #ffffff);
            box-shadow: 0 0 0 1px var(--secondaryColor, rgba(255,255,255,0.1));
          }

          .${scopeId}-input-field:disabled {
            background: rgba(255, 255, 255, 0.02);
            color: rgba(255, 255, 255, 0.4);
            border-color: rgba(255, 255, 255, 0.05);
            cursor: not-allowed;
          }

          select.${scopeId}-input-field {
            padding-right: 20px;
            appearance: none;
            background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>");
            background-repeat: no-repeat;
            background-position: right 12px center;
            background-size: 14px;
          }

          /* --- Footer Action Area --- */
          .${scopeId}-footer-action {
            margin-top: 35px;
            padding-top: 20px;
            border-top: 1px solid rgba(255, 255, 255, 0.08);
          }

          .${scopeId}-submit-btn {
            width: 100%;
            padding: 14px;
            font-size: 0.8rem;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1px;
            border-radius: 4px;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            transition: all 0.2s ease;
            background: var(--text-primary, #ffffff);
            color: #050b11;
          }

          .${scopeId}-submit-btn:hover {
            filter: brightness(1.1);
            transform: translateY(-1px);
          }

          /* --- Small / Side View Responsiveness --- */
          @media (max-width: 576px) {
            .${scopeId}-form-grid {
              grid-template-columns: 1fr;
            }
            .${scopeId}-col-full, .${scopeId}-form-grid > div {
              grid-column: span 1;
            }
            .${scopeId}-wrapper { padding: 16px; }
          }
        `}</style>

        <div className={`${scopeId}-wrapper`}>
          {/* Header Panel */}
          <header className={`${scopeId}-header-block`}>
            <button className={`${scopeId}-back-btn`} onClick={() => appContext?.navBack?.()}>
              <FiArrowLeft size={16} />
            </button>
            <div>
              <h1 className={`${scopeId}-panel-title`}>Profile Settings</h1>
              <p className={`${scopeId}-panel-subtitle`}>Modify your verified background identity variables.</p>
            </div>
          </header>

          {/* Core Structured Form Grid */}
          <div className={`${scopeId}-form-grid`}>
            {/* First Name */}
            <div className={`${scopeId}-field-group`}>
              <label className={`${scopeId}-label`}>First Name</label>
              <div className={`${scopeId}-input-container`}>
                <span className={`${scopeId}-icon`}><FiUser size={14} /></span>
                <input 
                  type="text" 
                  className={`${scopeId}-input-field`} 
                  value={firstName} 
                  onChange={(e) => setFirstName(e.target.value)} 
                />
              </div>
            </div>

            {/* Middle Name */}
            <div className={`${scopeId}-field-group`}>
              <label className={`${scopeId}-label`}>Middle Name</label>
              <div className={`${scopeId}-input-container`}>
                <span className={`${scopeId}-icon`}><FiUser size={14} /></span>
                <input 
                  type="text" 
                  className={`${scopeId}-input-field`} 
                  value={middleName} 
                  onChange={(e) => setMiddleName(e.target.value)} 
                />
              </div>
            </div>

            {/* Last Name */}
            <div className={`${scopeId}-field-group ${scopeId}-col-full`}>
              <label className={`${scopeId}-label`}>Last Name</label>
              <div className={`${scopeId}-input-container`}>
                <span className={`${scopeId}-icon`}><FiUser size={14} /></span>
                <input 
                  type="text" 
                  className={`${scopeId}-input-field`} 
                  value={lastName} 
                  onChange={(e) => setLastName(e.target.value)} 
                />
              </div>
            </div>

            {/* Email (Disabled/Locked node) */}
            <div className={`${scopeId}-field-group ${scopeId}-col-full`}>
              <label className={`${scopeId}-label`}>Email Account Address (Immutable)</label>
              <div className={`${scopeId}-input-container`}>
                <span className={`${scopeId}-icon`}><FiMail size={14} /></span>
                <input 
                  type="email" 
                  className={`${scopeId}-input-field`} 
                  value={email} 
                  disabled 
                />
                <span style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.25)', display: 'flex' }}>
                  <FiLock size={14} />
                </span>
              </div>
            </div>

            {/* Phone */}
            <div className={`${scopeId}-field-group ${scopeId}-col-full`}>
              <label className={`${scopeId}-label`}>Mobile Phone Number</label>
              <div className={`${scopeId}-input-container`}>
                <span className={`${scopeId}-icon`}><FiPhone size={14} /></span>
                <input 
                  type="text" 
                  className={`${scopeId}-input-field`} 
                  value={phone} 
                  onChange={(e) => setPhone(e.target.value)} 
                />
              </div>
            </div>

            {/* Physical Address */}
            <div className={`${scopeId}-field-group ${scopeId}-col-full`}>
              <label className={`${scopeId}-label`}>Physical Residential Address</label>
              <div className={`${scopeId}-input-container`}>
                <span className={`${scopeId}-icon`}><FiMapPin size={14} /></span>
                <input 
                  type="text" 
                  className={`${scopeId}-input-field`} 
                  value={physicalAddress} 
                  onChange={(e) => setPhysicalAddress(e.target.value)} 
                />
              </div>
            </div>

            {/* Date of Birth */}
            <div className={`${scopeId}-field-group`}>
              <label className={`${scopeId}-label`}>Date of Birth</label>
              <div className={`${scopeId}-input-container`}>
                <span className={`${scopeId}-icon`}><FiCalendar size={14} /></span>
                <input 
                  type="date" 
                  className={`${scopeId}-input-field`} 
                  value={dob} 
                  onChange={(e) => setDob(e.target.value)} 
                />
              </div>
            </div>

            {/* Gender */}
            <div className={`${scopeId}-field-group`}>
              <label className={`${scopeId}-label`}>Gender</label>
              <div className={`${scopeId}-input-container`}>
                <span className={`${scopeId}-icon`}><FiUser size={14} /></span>
                <select 
                  className={`${scopeId}-input-field`} 
                  value={gender} 
                  onChange={(e) => setGender(e.target.value)}
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>
          </div>

          {/* Submit Action Area */}
          <footer className={`${scopeId}-footer-action`}>
            <button 
              className={`${scopeId}-submit-btn`} 
              onClick={handleUpdate}
            >
              <FiSave size={14} />
              <span>Update Data Profile</span>
            </button>
          </footer>
        </div>
      </>
    );
  } else {
    return (
      <div style={{ minHeight: '30vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {getInlineLoader()}
      </div>
    );
  }
}